import { normalizeEmployeeLogin } from "@/lib/auth";
import { searchEmployees } from "@/lib/projectOverview";
import { buildNoteReferenceTokenText } from "@/projectOverview/notes/noteReferences";
import type { DraftNoteReference } from "@/projectOverview/notes/types";

export interface ReplyEmployeeCandidate {
  id: string;
  label: string;
  subtitle?: string | null;
}

export interface ReplyAuthor {
  login?: string | null;
  display?: string | null;
}

export interface MentionSeed {
  key: string;
  text: string;
  references: DraftNoteReference[];
}

function collapse(value: string | null | undefined): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** „Florian Borgstedt“ und „Borgstedt, Florian“ gelten als derselbe Name. */
function nameTokens(value: string | null | undefined): string[] {
  return collapse(value)
    .replace(/[,.()]/g, " ")
    .split(" ")
    .filter((token) => token.length >= 2)
    .sort();
}

function samePersonName(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = nameTokens(a);
  const right = nameTokens(b);
  return (
    left.length > 0 &&
    left.length === right.length &&
    left.every((token, index) => token === right[index])
  );
}

function employeeMatchesLogin(
  candidate: ReplyEmployeeCandidate,
  login: string,
  loginLocal: string,
): boolean {
  const ids = [candidate.id, candidate.subtitle]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  if (login && ids.some((id) => id === login)) return true;
  if (!loginLocal) return false;
  return ids.some((id) => normalizeEmployeeLogin(id) === loginLocal);
}

/** Eindeutige Person für eine Antwort-Erwähnung. Bei Gleichstand keine Wahl. */
export function pickReplyEmployee(
  candidates: ReplyEmployeeCandidate[],
  author: ReplyAuthor,
): ReplyEmployeeCandidate | null {
  const display = collapse(author.display);
  const login = String(author.login || "").trim().toLowerCase();
  const loginLocal = normalizeEmployeeLogin(login);

  const usable = candidates.filter((candidate) => candidate.id && candidate.label.trim());
  const loginHits = usable.filter((candidate) =>
    employeeMatchesLogin(candidate, login, loginLocal),
  );
  if (loginHits.length === 1) return loginHits[0]!;
  if (loginHits.length > 1) {
    const named = display
      ? loginHits.filter((candidate) => samePersonName(candidate.label, author.display))
      : [];
    return named.length === 1 ? named[0]! : null;
  }

  if (!display) return null;
  const exactName = usable.filter((candidate) => samePersonName(candidate.label, author.display));
  return exactName.length === 1 ? exactName[0]! : null;
}

function labelFromEmail(email: string): string {
  const local = email.split("@")[0]?.trim();
  return local || email;
}

export async function resolveReplyEmployee(
  author: ReplyAuthor,
  search: (
    query: string,
    limit?: number,
  ) => Promise<Array<{ id: string; label?: string | null; subtitle?: string | null }>> = searchEmployees,
): Promise<ReplyEmployeeCandidate | null> {
  const display = String(author.display || "").trim();
  const login = String(author.login || "").trim();
  const nameParts = display
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter((part) => part.length >= 3);
  const queries = [display, ...nameParts, normalizeEmployeeLogin(login)].filter(
    (query, index, all) => query.length >= 2 && all.indexOf(query) === index,
  );

  let pool: ReplyEmployeeCandidate[] = [];
  for (const query of queries) {
    const rows = await search(query, 30);
    const seen = new Set(pool.map((row) => row.id));
    for (const row of rows) {
      const id = String(row?.id || "").trim();
      const label = String(row?.label || "").trim();
      if (!id || !label || seen.has(id)) continue;
      seen.add(id);
      pool.push({ id, label, subtitle: row.subtitle });
    }
    const picked = pickReplyEmployee(pool, author);
    if (picked) return picked;
  }

  if (login.includes("@")) {
    return {
      id: login,
      label: display || labelFromEmail(login),
      subtitle: login,
    };
  }
  return null;
}

/** `@Name ` plus Referenz, Cursor steht danach zum Weiterschreiben. */
export function buildEmployeeMentionSeed(
  employee: ReplyEmployeeCandidate,
  key: string,
): MentionSeed | null {
  const label = employee.label.trim();
  const refId = employee.id.trim();
  if (!label || !refId) return null;
  const token = buildNoteReferenceTokenText("@", label);
  const text = `${token} `;
  return {
    key,
    text,
    references: [
      {
        type: "employee",
        ref_id: refId,
        label,
        trigger: "@",
        token_text: token,
        start: 0,
        end: token.length,
      },
    ],
  };
}

/**
 * Setzt die Antwort-Erwähnung an den Anfang.
 * Eine frühere Antwort-Erwähnung wird ersetzt, bereits getippter Text bleibt stehen.
 * `null`, wenn diese Person schon im Text steht.
 */
export function applyMentionSeedToDraft(
  currentText: string,
  currentRefs: DraftNoteReference[],
  previousSeedText: string | null,
  seed: MentionSeed,
): { text: string; references: DraftNoteReference[]; cursor: number } | null {
  const token = seed.references[0]?.token_text || "";
  if (!token) return null;

  let nextText: string;
  let baseRefs: DraftNoteReference[];

  if (!currentText.trim() || (previousSeedText != null && currentText === previousSeedText)) {
    nextText = seed.text;
    baseRefs = [];
  } else if (previousSeedText && currentText.startsWith(previousSeedText)) {
    const rest = currentText.slice(previousSeedText.length);
    nextText = `${seed.text}${rest}`;
    const cut = previousSeedText.length;
    baseRefs = currentRefs
      .filter((ref) => ref.start >= cut)
      .map((ref) => ({ ...ref, start: ref.start - cut, end: ref.end - cut }));
  } else if (currentText.includes(token)) {
    return null;
  } else {
    const rest = currentText.replace(/^\s+/, "");
    const stripped = currentText.length - rest.length;
    nextText = `${seed.text}${rest}`;
    baseRefs = currentRefs
      .filter((ref) => ref.start >= stripped)
      .map((ref) => ({
        ...ref,
        start: ref.start - stripped,
        end: ref.end - stripped,
      }));
  }

  const delta = seed.text.length;
  return {
    text: nextText,
    references: [
      ...seed.references,
      ...baseRefs.map((ref) => ({
        ...ref,
        start: ref.start + delta,
        end: ref.end + delta,
      })),
    ],
    cursor: nextText.length,
  };
}
